import { useState, useEffect } from 'react';
import GroupLogo from '../components/GroupLogo';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDownLeft, ArrowUpRight, Users, Clock, ChevronRight, ChevronDown, Zap, X, AlertTriangle, Calendar, QrCode, Camera, Smartphone, Share2, Vault, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import NXCIcon from '../components/NXCIcon';
import { formatNXC } from '../lib/formatNXC';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { AvatarStack } from '../components/ui/avatar-initials';
import EmptyState from '../components/EmptyState';
import { EmptyGroups } from '../components/illustrations';
import LiveSessionBanner from '../components/LiveSessionBanner';
import SettleSheet from '../components/SettleSheet';
import QrScanSheet from '../components/QrScanSheet';
import PayByPhoneSheet from '../components/PayByPhoneSheet';
import PersonalQrCard from '../components/PersonalQrCard';
import ShareInviteSheet from '../components/ShareInviteSheet';
import useGroupInvite from '../lib/useGroupInvite';
import useDraftAutoSave from '../hooks/useDraftAutoSave';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const DOT_COLORS = ['bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-lime-500'];
// Deterministic color dot per group so same-named groups are distinguishable.
export const groupDotColor = (id) => DOT_COLORS[(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % DOT_COLORS.length];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { onEvent } = useRealtime();
  const [dashboard, setDashboard] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loansData, setLoansData] = useState(null);
  const [cashBoxes, setCashBoxes] = useState(null); // iter241 — Holder Cash View
  // iter244 — hero breakdown + empty-box collapse
  const [showPositions, setShowPositions] = useState(false);
  const [showEmptyBoxes, setShowEmptyBoxes] = useState(false);
  // iter242 — Box Count Check
  const [countBox, setCountBox] = useState(null);
  const [countValue, setCountValue] = useState('');
  const [countBusy, setCountBusy] = useState(false);

  const submitCashCount = async () => {
    if (countBusy || !countBox) return;
    setCountBusy(true);
    try {
      const res = await api.post(`/groups/${countBox.group_id}/cash-count`, {
        counted_amount: Number(countValue),
      });
      const d = res.data;
      if (d.matched) toast.success(`Perfect match — box has exactly ₹${Math.round(d.expected_amount).toLocaleString('en-IN')}`);
      else toast.warning(`Off by ₹${Math.abs(Math.round(d.diff)).toLocaleString('en-IN')} (${d.diff > 0 ? 'extra' : 'short'}) — expected ₹${Math.round(d.expected_amount).toLocaleString('en-IN')}`);
      setCountBox(null);
      setCountValue('');
      const boxes = await api.get('/my/cash-boxes').catch(() => null);
      if (boxes) setCashBoxes(boxes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record count');
    } finally {
      setCountBusy(false);
    }
  };
  const [showLoansDetail, setShowLoansDetail] = useState(false);
  
  // Quick Split state
  const [showQuickSplit, setShowQuickSplit] = useState(false);
  const [settleSheetTab, setSettleSheetTab] = useState(null); // null | 'owe' | 'owed'
  // iter125 — quick-pay cards on Home
  const [qrScanOpen, setQrScanOpen] = useState(false);
  const [phonePayOpen, setPhonePayOpen] = useState(false);
  const [showMyQrOpen, setShowMyQrOpen] = useState(false);

  // iter127 — universal Share-Invite sheet hook
  const invite = useGroupInvite();

  // iter125b — handle PWA shortcut deep-links: /?action=scan|my-qr|quick-split
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'scan') setQrScanOpen(true);
    else if (action === 'my-qr') setShowMyQrOpen(true);
    else if (action === 'quick-split') setShowQuickSplit(true);
    if (action) {
      // Clean the URL so a refresh doesn't re-open the sheet
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [quickSplitData, setQuickSplitData] = useState({
    group_id: '',
    amount: '',
    description: '',
    category: 'sports',
    split_among: [],
  });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Durability: persist Quick Split draft until submitted
  const { clearDraft: clearQuickSplitDraft } = useDraftAutoSave({
    formKey: 'quickSplit',
    value: quickSplitData,
    onRestore: (saved) => setQuickSplitData((prev) => ({ ...prev, ...saved })),
    enabled: showQuickSplit,
  });

  useEffect(() => {
    fetchData();
  }, []);

  // After signup→login or login from /welcome, honor any pending invite by routing to /join/<code>
  useEffect(() => {
    try {
      const pending = localStorage.getItem('pending_invite_code');
      if (pending) {
        localStorage.removeItem('pending_invite_code');
        navigate(`/join/${pending}`, { replace: true });
      }
    } catch { /* ignore */ }
  }, [navigate]);

  // Live refresh: any event that could change dashboard counts re-fetches
  useEffect(() => {
    return onEvent(
      [
        'expense_pending',
        'expense_approved',
        'expense_rejected',
        'expense_reversed',
        'expense_deleted',
        'coin_transfer_request',
        'coin_transfer_approved',
        'coin_transfer_rejected',
        'coins_allotted',
        'fine_issued',
        'fine_reversed',
        'recurring_expense',
        'pledge_received',
        'loan_disbursed',
        'loan_repaid',
        'session_created',
        'session_cancelled',
        'member_joined',
        'member_removed',
      ],
      () => { fetchData(); }
    );
  }, [onEvent]);

  const fetchData = async () => {
    try {
      const [dashboardRes, groupsRes, loansRes, boxesRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/groups'),
        api.get('/loans/dashboard').catch(() => ({ data: null })),
        api.get('/my/cash-boxes').catch(() => ({ data: null })),
      ]);
      setDashboard(dashboardRes.data);
      setGroups(groupsRes.data);
      setLoansData(loansRes.data);
      setCashBoxes(boxesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => formatNXC(amount);

  const handleGroupSelect = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    setSelectedGroup(group);
    setQuickSplitData({
      ...quickSplitData,
      group_id: groupId,
      split_among: group?.member_ids || [],
    });
  };

  const toggleMemberInSplit = (memberId) => {
    setQuickSplitData(prev => ({
      ...prev,
      split_among: prev.split_among.includes(memberId)
        ? prev.split_among.filter(id => id !== memberId)
        : [...prev.split_among, memberId],
    }));
  };

  const handleQuickSplit = async (e) => {
    e.preventDefault();
    if (!quickSplitData.group_id || !quickSplitData.amount || !quickSplitData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (quickSplitData.split_among.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/quick-split', {
        group_id: quickSplitData.group_id,
        amount: parseFloat(quickSplitData.amount),
        description: quickSplitData.description,
        category: quickSplitData.category,
        split_among: quickSplitData.split_among,
      });
      
      if (response.data.auto_approved) {
        toast.success('Expense added and auto-approved!');
      } else {
        toast.success('Expense added! Waiting for admin approval.');
      }
      
      setShowQuickSplit(false);
      setQuickSplitData({
        group_id: '',
        amount: '',
        description: '',
        category: 'sports',
        split_among: [],
      });
      setSelectedGroup(null);
      clearQuickSplitDraft();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRepayLoanFromDashboard = async (loanId, gid, outstanding, name) => {
    const input = window.prompt(
      `Repay how much for ${name || 'loan'}? (Outstanding: ${formatNXC(outstanding)})\nLeave blank to repay full.`,
      String(Math.round(outstanding))
    );
    if (input === null) return;
    const amt = input.trim() === '' ? outstanding : parseFloat(input);
    if (Number.isNaN(amt) || amt <= 0) {
      toast.error('Enter a valid positive amount');
      return;
    }
    try {
      const body = amt < outstanding ? { amount: amt } : {};
      const res = await api.post(`/groups/${gid}/fund/loans/${loanId}/repay`, body);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record repayment');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-40 rounded-2xl" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Live session banner — surfaces in-progress sessions across groups */}
      <LiveSessionBanner variant="home" />

      {/* Quick Split FAB */}
      {groups.length > 0 && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowQuickSplit(true)}
          data-testid="quick-split-fab"
          className="fixed bottom-36 right-4 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg fab-glow flex items-center justify-center"
        >
          <Zap className="w-6 h-6" />
        </motion.button>
      )}

      {/* iter270 — desktop 2-col grid (mobile order unchanged: columns stack) */}
      <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start" data-testid="dashboard-columns">
      <div className="space-y-6">
      {/* iter244 — merged hero: net balance + to-pay/receive + by-group breakdown */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="p-6 border-2 rounded-2xl bg-card">
          <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Net Balance
          </p>
          <p 
            className={`font-display text-5xl font-bold tracking-tight amount-display ${
              dashboard?.net_balance >= 0 ? 'text-positive' : 'text-negative'
            }`}
            data-testid="net-balance"
          >
            {dashboard?.net_balance >= 0 ? '+' : ''}{formatCurrency(dashboard?.net_balance || 0)}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {dashboard?.net_balance >= 0 
              ? "You'll receive this amount overall" 
              : "You need to pay this amount overall"}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <button
              type="button"
              onClick={() => setSettleSheetTab('owe')}
              className="text-left p-3 rounded-xl border border-border hover:border-primary/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="to-pay-card-btn"
            >
              <div className="flex items-center gap-1.5 mb-1" data-testid="to-pay-card">
                <ArrowUpRight className="w-3.5 h-3.5 text-negative" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">To Pay</span>
              </div>
              <p className="font-display text-xl font-bold text-negative amount-display">
                {formatCurrency(dashboard?.total_to_pay || 0)}
              </p>
              {(dashboard?.total_to_pay || 0) > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-wider">Tap to settle →</p>
              )}
            </button>
            <button
              type="button"
              onClick={() => setSettleSheetTab('owed')}
              className="text-left p-3 rounded-xl border border-border hover:border-primary/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="to-receive-card-btn"
            >
              <div className="flex items-center gap-1.5 mb-1" data-testid="to-receive-card">
                <ArrowDownLeft className="w-3.5 h-3.5 text-positive" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">To Receive</span>
              </div>
              <p className="font-display text-xl font-bold text-positive amount-display">
                {formatCurrency(dashboard?.total_to_receive || 0)}
              </p>
              {(dashboard?.total_to_receive || 0) > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-wider">Tap to show QR →</p>
              )}
            </button>
          </div>

          {(dashboard?.group_positions?.length || 0) > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowPositions((v) => !v)}
                data-testid="net-position-toggle"
                className="w-full mt-3 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
              >
                {showPositions ? 'Hide breakdown' : `By group (${dashboard.group_positions.length})`}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPositions ? 'rotate-180' : ''}`} />
              </button>
              {showPositions && (
                <div className="space-y-1.5 mt-1" data-testid="net-position-widget">
                  {dashboard.group_positions.map((p) => (
                    <button
                      key={p.group_id}
                      type="button"
                      onClick={() => navigate(`/groups/${p.group_id}`)}
                      data-testid={`net-position-${p.group_id}`}
                      className="w-full flex items-center justify-between gap-2 p-2 rounded-xl border border-border hover:bg-accent transition-colors text-left"
                    >
                      <p className="font-semibold text-sm truncate flex items-center gap-2 min-w-0">
                        {p.logo_url
                          ? <GroupLogo logoUrl={p.logo_url} size="xs" />
                          : p.emoji
                          ? <span className="shrink-0 text-sm leading-none">{p.emoji}</span>
                          : <span className={`w-2 h-2 rounded-full shrink-0 ${groupDotColor(p.group_id)}`} />}
                        <span className="truncate">{p.group_name}</span>
                      </p>
                      <p className={`font-display font-bold text-sm tabular-nums shrink-0 ${p.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {p.net >= 0 ? 'To get ₹' : 'To pay ₹'}{Math.abs(Math.round(p.net)).toLocaleString('en-IN')}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </motion.div>

      {/* iter125 — Quick Pay cards (iter272 — renamed to Settle-* with hints) */}
      <div className="grid grid-cols-3 gap-2" data-testid="quick-pay-row">
        <QuickPayCard
          icon={Camera}
          label="Settle by QR"
          hint="Scan a friend's QR to clear what you owe"
          accent="primary"
          onClick={() => setQrScanOpen(true)}
          testId="quick-pay-scan"
        />
        <QuickPayCard
          icon={Smartphone}
          label="Settle by number"
          hint="Type a friend's number — no QR needed"
          accent="violet"
          onClick={() => setPhonePayOpen(true)}
          testId="quick-pay-by-phone"
        />
        <QuickPayCard
          icon={QrCode}
          label="Get paid"
          hint="Show your QR so friends can settle with you"
          accent="emerald"
          onClick={() => setShowMyQrOpen(true)}
          testId="quick-pay-show-qr"
        />
      </div>

      {/* iter241 — Holder Cash View: boxes this user keeps (iter244: empty boxes collapsed, negatives flagged) */}
      {(cashBoxes?.boxes?.length || 0) > 0 && (() => {
        const activeBoxes = cashBoxes.boxes.filter((b) => Math.round(b.cash) !== 0);
        const emptyBoxes = cashBoxes.boxes.filter((b) => Math.round(b.cash) === 0);
        const renderBox = (box) => {
          const negative = box.cash < 0;
          return (
              <button
                key={box.group_id}
                type="button"
                onClick={() => navigate(`/groups/${box.group_id}`)}
                data-testid={`cash-box-${box.group_id}`}
                className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border transition-colors text-left ${
                  negative ? 'border-rose-500/50 bg-rose-500/5 hover:bg-rose-500/10' : 'border-border hover:bg-accent'
                }`}
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                    {box.logo_url
                      ? <GroupLogo logoUrl={box.logo_url} size="xs" />
                      : box.emoji
                      ? <span className="shrink-0 text-sm leading-none">{box.emoji}</span>
                      : <span className={`w-2 h-2 rounded-full shrink-0 ${groupDotColor(box.group_id)}`} />}
                    {box.group_name}
                    {box.is_default_holder && <span className="text-xs text-muted-foreground font-normal"> · default holder</span>}
                  </p>
                  {negative && (
                    <p className="text-xs text-rose-600 font-semibold mt-0.5 flex items-center gap-1" data-testid={`cash-box-negative-${box.group_id}`}>
                      <AlertTriangle className="w-3 h-3" /> Negative box — needs reconcile
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); navigate(`/groups/${box.group_id}/reconcile`); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/groups/${box.group_id}/reconcile`); } }}
                        className="underline decoration-dotted hover:decoration-solid"
                        data-testid={`cash-box-fix-${box.group_id}`}
                      >
                        Fix →
                      </span>
                    </p>
                  )}
                  {(box.pending_in_count > 0 || box.pending_out_count > 0) && (
                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                      {box.pending_in_count > 0 && (
                        <span className="text-green-600 flex items-center gap-0.5">
                          <ArrowDownToLine className="w-3 h-3" /> {box.pending_in_count} in · ₹{Math.round(box.pending_in_amount).toLocaleString('en-IN')}
                        </span>
                      )}
                      {box.pending_out_count > 0 && (
                        <span className="text-orange-600 flex items-center gap-0.5">
                          <ArrowUpFromLine className="w-3 h-3" /> {box.pending_out_count} out · ₹{Math.round(box.pending_out_amount).toLocaleString('en-IN')}
                        </span>
                      )}
                    </p>
                  )}
                  {box.last_count && (
                    <p className={`text-xs mt-0.5 ${box.last_count.matched && box.last_count.expected_amount === box.cash ? 'text-green-600' : 'text-muted-foreground'}`} data-testid={`cash-box-last-count-${box.group_id}`}>
                      {box.last_count.matched ? '✓' : '⚠'} Counted ₹{Math.round(box.last_count.counted_amount).toLocaleString('en-IN')}
                      {!box.last_count.matched && ` (off ₹${Math.abs(Math.round(box.last_count.diff)).toLocaleString('en-IN')})`}
                      {' · '}{new Date(box.last_count.counted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </p>
                  )}
                </div>
                <p className={`font-display font-bold tabular-nums shrink-0 ${negative ? 'text-rose-600' : ''}`} data-testid={`cash-box-amount-${box.group_id}`}>
                  ₹{Math.round(box.cash).toLocaleString('en-IN')}
                </p>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setCountBox(box); setCountValue(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setCountBox(box); setCountValue(''); } }}
                  data-testid={`cash-box-count-btn-${box.group_id}`}
                  className="shrink-0 px-2.5 py-1 rounded-full border border-primary/40 text-xs font-bold text-primary hover:bg-primary/10 transition-colors"
                >
                  Count
                </span>
              </button>
          );
        };
        return (
        <Card className="p-4 border-2 rounded-2xl" data-testid="cash-boxes-card">
          <div className="flex items-center justify-between mb-3">
            <p className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-2">
              <Vault className="w-4 h-4 text-primary" /> Cash Boxes You Hold
            </p>
            <p className="font-display font-bold tabular-nums" data-testid="cash-boxes-total">
              ₹{Math.round(cashBoxes.total_cash).toLocaleString('en-IN')}
            </p>
          </div>
          <div className="space-y-2">
            {activeBoxes.map(renderBox)}
            {emptyBoxes.length > 0 && (
              <button
                type="button"
                onClick={() => setShowEmptyBoxes((v) => !v)}
                data-testid="empty-boxes-toggle"
                className="w-full py-2 rounded-xl border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
              >
                {showEmptyBoxes ? 'Hide empty boxes' : `+${emptyBoxes.length} empty box${emptyBoxes.length === 1 ? '' : 'es'}`}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showEmptyBoxes ? 'rotate-180' : ''}`} />
              </button>
            )}
            {showEmptyBoxes && emptyBoxes.map(renderBox)}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            What each box should physically contain right now. Count and compare anytime.
          </p>
        </Card>
        );
      })()}
      </div>

      <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-4">
        <Card 
          className="p-4 border-2 rounded-2xl cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => navigate('/groups')}
          data-testid="groups-count-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-display text-2xl font-bold">{dashboard?.groups_count || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Groups</p>
            </div>
          </div>
        </Card>

        <Card 
          className="p-4 border-2 rounded-2xl cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => navigate('/approvals')}
          data-testid="pending-approvals-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="font-display text-2xl font-bold">{dashboard?.pending_approvals || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Loans mini-dashboard — surfaces overdue + due-soon loans across ALL groups */}
      {loansData && (loansData.summary?.total_outstanding > 0 || loansData.loans?.length > 0) && (
        <button
          type="button"
          onClick={() => setShowLoansDetail(true)}
          data-testid="loans-dashboard-card"
          className="w-full text-left rounded-2xl border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/5 via-background to-background p-4 hover:border-blue-500/50 transition-colors"
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <NXCIcon className="w-4 h-4 text-blue-700" />
              <span className="text-xs uppercase tracking-wider font-bold text-blue-700/90">
                Loans · {loansData.summary.groups_count} group{loansData.summary.groups_count === 1 ? '' : 's'}
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Outstanding</div>
              <div className="text-base sm:text-lg font-display font-bold" data-testid="loans-total-outstanding">
                {formatNXC(loansData.summary.total_outstanding)}
              </div>
            </div>
            <div>
              <div className="text-xs text-amber-700/90 uppercase tracking-wide font-bold flex items-center gap-1">
                <Calendar className="w-3 h-3" />Due 7d
              </div>
              <div className="text-base sm:text-lg font-display font-bold text-amber-700" data-testid="loans-due-soon">
                {formatNXC(loansData.summary.due_soon_amount)}
              </div>
              <div className="text-xs text-muted-foreground">{loansData.summary.due_soon_count} loan{loansData.summary.due_soon_count === 1 ? '' : 's'}</div>
            </div>
            <div>
              <div className="text-xs text-red-700/90 uppercase tracking-wide font-bold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />Overdue
              </div>
              <div className="text-base sm:text-lg font-display font-bold text-red-700" data-testid="loans-overdue">
                {formatNXC(loansData.summary.overdue_amount)}
              </div>
              <div className="text-xs text-muted-foreground">{loansData.summary.overdue_count} loan{loansData.summary.overdue_count === 1 ? '' : 's'}</div>
            </div>
          </div>
          {(loansData.summary.user_is_creditor > 0 || loansData.summary.user_is_debtor > 0) && (
            <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between gap-3 text-xs">
              {loansData.summary.user_is_creditor > 0 && (
                <span className="text-emerald-700 font-semibold" data-testid="loans-i-am-creditor">
                  Owed to you: {formatNXC(loansData.summary.user_is_creditor)}
                </span>
              )}
              {loansData.summary.user_is_debtor > 0 && (
                <span className="text-orange-700 font-semibold" data-testid="loans-i-owe">
                  You owe: {formatNXC(loansData.summary.user_is_debtor)}
                </span>
              )}
            </div>
          )}
        </button>
      )}

      {/* Loans detail dialog — full list with Repay Now buttons */}
      {/* iter242 — Box Count dialog */}
      <Dialog open={!!countBox} onOpenChange={(v) => !v && setCountBox(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="cash-count-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Count the box — {countBox?.group_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Count the physical cash and enter what you found. Expected: <span className="font-bold text-foreground">₹{Math.round(countBox?.cash || 0).toLocaleString('en-IN')}</span>
            </p>
            <Input
              type="number"
              min={0}
              value={countValue}
              onChange={(e) => setCountValue(e.target.value)}
              placeholder="I counted ₹…"
              data-testid="cash-count-input"
              className="h-12 border-2 rounded-xl font-bold text-lg text-center"
              autoFocus
            />
            <Button
              className="w-full rounded-full font-bold"
              disabled={countBusy || countValue === ''}
              onClick={submitCashCount}
              data-testid="cash-count-submit"
            >
              {countBusy ? 'Recording…' : 'Record count'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLoansDetail} onOpenChange={setShowLoansDetail}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="loans-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <NXCIcon className="w-5 h-5 text-blue-700" />
              All Open Loans
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {!loansData?.loans?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No open loans across your groups.</p>
            ) : (
              loansData.loans.map((L) => {
                const isMtg = L.direction === 'member_to_group';
                const tone = L.is_overdue ? 'red' : (L.is_due_soon ? 'amber' : 'blue');
                const toneClass = {
                  red: 'border-red-500/40 bg-red-500/5',
                  amber: 'border-amber-500/40 bg-amber-500/5',
                  blue: 'border-blue-500/30 bg-blue-500/5',
                }[tone];
                return (
                  <div
                    key={L.id}
                    className={`p-3 rounded-xl border-2 ${toneClass}`}
                    data-testid={`loans-detail-row-${L.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">{L.user_name}</span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-bold uppercase ${
                              isMtg ? 'bg-emerald-500/15 text-emerald-700' : 'bg-orange-500/15 text-orange-700'
                            }`}
                          >
                            {isMtg ? 'Owed by group' : 'Owed to group'}
                          </span>
                          {L.is_overdue && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-700 font-bold uppercase">
                              {Math.abs(L.days_until_due)}d overdue
                            </span>
                          )}
                          {L.is_due_soon && !L.is_overdue && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-700 font-bold uppercase">
                              {L.days_until_due === 0 ? 'today' : `${L.days_until_due}d`}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setShowLoansDetail(false); navigate(`/groups/${L.group_id}`); }}
                          className="text-xs text-muted-foreground hover:text-primary mt-0.5 block truncate"
                        >
                          {L.group_name} ›
                        </button>
                        {L.note && <div className="text-xs text-muted-foreground mt-0.5 truncate">{L.note}</div>}
                        {L.expected_repayment_date && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Due {L.expected_repayment_date}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold text-base">{formatNXC(L.outstanding)}</div>
                        {L.loan_status === 'partial' && (
                          <div className="text-xs text-muted-foreground">
                            {Math.round(L.repaid)}/{Math.round(L.principal)} repaid
                          </div>
                        )}
                      </div>
                    </div>
                    {L.user_is_admin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRepayLoanFromDashboard(L.id, L.group_id, L.outstanding, L.user_name)}
                        data-testid={`loans-detail-repay-${L.id}`}
                        className="rounded-full h-8 text-xs px-3 mt-2 w-full"
                      >
                        Repay Now
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Groups */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold">Your Groups</h2>
          <button 
            onClick={() => navigate('/groups')}
            className="text-sm text-primary font-semibold"
            data-testid="view-all-groups-btn"
          >
            View All
          </button>
        </div>

        <div className="space-y-3">
          {groups.length === 0 ? (
            <EmptyState
              illustration={EmptyGroups}
              title="No groups yet"
              description="Create your first group to start tracking expenses with your crew."
              testId="no-groups-message"
              cta={
                <Button
                  onClick={() => navigate('/groups')}
                  className="rounded-full"
                  data-testid="create-first-group-btn"
                >
                  Create your first group
                </Button>
              }
            />
          ) : (
            groups.slice(0, 3).map((group, index) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Card
                  className="p-4 border-2 rounded-2xl cursor-pointer hover:border-primary/40 hover:shadow-md transition-all expense-item"
                  onClick={() => navigate(`/groups/${group.id}`)}
                  onContextMenu={(e) => { e.preventDefault(); invite.openShareMenu(group); }}
                  data-testid={`group-card-${group.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                          group.group_type === 'sports'
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                            : group.group_type === 'trip'
                            ? 'bg-sky-500/15 text-sky-700 dark:text-sky-400'
                            : group.group_type === 'event'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                            : 'bg-violet-500/15 text-violet-700 dark:text-violet-400'
                        }`}>
                          {group.type_meta?.label || group.group_type}
                        </span>
                        {group.logo_url
                          ? <GroupLogo logoUrl={group.logo_url} size="sm" testId={`group-logo-${group.id}`} />
                          : group.emoji
                          ? <span className="shrink-0 text-base leading-none" data-testid={`group-emoji-${group.id}`}>{group.emoji}</span>
                          : <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${groupDotColor(group.id)}`} />}
                        <h3 className="font-display font-bold truncate">{group.name}</h3>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <AvatarStack users={(group.members || []).slice(0, 4)} max={4} size="xs" />
                        <p className="text-xs text-muted-foreground">
                          {group.members?.length || 0} members
                          {group.user_coins != null && group.user_coins !== 0 && (
                            <span className="ml-2 font-semibold text-primary" data-testid={`group-coins-${group.id}`}>
                              · bal {group.currency_name && group.currency_name !== 'Coins'
                                ? `${Math.round(group.user_coins).toLocaleString('en-IN')} ${group.currency_name}`
                                : `₹${Math.round(group.user_coins).toLocaleString('en-IN')}`}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        {group.user_balance?.to_pay > 0 && (
                          <p className="text-sm font-bold text-negative font-display">
                            −{formatCurrency(group.user_balance.to_pay)}
                          </p>
                        )}
                        {group.user_balance?.to_receive > 0 && (
                          <p className="text-sm font-bold text-positive font-display">
                            +{formatCurrency(group.user_balance.to_receive)}
                          </p>
                        )}
                        {(!group.user_balance?.to_pay && !group.user_balance?.to_receive) && (
                          <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-bold uppercase tracking-wider">Square ✓</span>
                        )}
                      </div>
                      {/* iter127 — Share-invite icon: opens unified ShareInviteSheet. Tap stops nav. */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); invite.openShareMenu(group); }}
                        className="p-2 rounded-full hover:bg-primary/10 active:bg-primary/15 text-muted-foreground hover:text-primary transition-colors"
                        title="Share invite link"
                        aria-label={`Share invite to ${group.name}`}
                        data-testid={`group-card-share-${group.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))
          )}
        </div>
      </div>
      </div>
      </div>

      {/* Quick Split Modal */}
      <Dialog open={showQuickSplit} onOpenChange={setShowQuickSplit}>
        <DialogContent className="sm:max-w-md" data-testid="quick-split-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Quick Split
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleQuickSplit} className="space-y-5 mt-4">
            {/* Group Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Select Group</Label>
              <Select
                value={quickSplitData.group_id}
                onValueChange={handleGroupSelect}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="quick-split-group-select">
                  <SelectValue placeholder="Choose a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
              <Input
                type="number"
                value={quickSplitData.amount}
                onChange={(e) => setQuickSplitData({ ...quickSplitData, amount: e.target.value })}
                placeholder="0"
                data-testid="quick-split-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Description</Label>
              <Input
                value={quickSplitData.description}
                onChange={(e) => setQuickSplitData({ ...quickSplitData, description: e.target.value })}
                placeholder="e.g., Court booking"
                data-testid="quick-split-description-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>

            {/* Members Selection (only show if group selected) */}
            {selectedGroup && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  Who's splitting? ({quickSplitData.split_among.length} selected)
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedGroup.members?.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer hover:border-primary/50"
                      onClick={() => toggleMemberInSplit(member.id)}
                    >
                      <Checkbox
                        checked={quickSplitData.split_among.includes(member.id)}
                        onCheckedChange={() => toggleMemberInSplit(member.id)}
                        data-testid={`quick-split-member-${member.id}`}
                      />
                      <span className="font-medium">{member.name}</span>
                      {member.id === user?.id && (
                        <span className="text-xs text-primary">(You - paying)</span>
                      )}
                    </div>
                  ))}
                </div>
                {quickSplitData.split_among.length > 0 && quickSplitData.amount && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Each person pays: {formatCurrency(parseFloat(quickSplitData.amount) / quickSplitData.split_among.length)}
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !quickSplitData.group_id}
              data-testid="quick-split-submit-btn"
              className="w-full h-14 rounded-full font-bold uppercase tracking-wider"
            >
              {submitting ? 'Adding...' : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Split Now
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter121: cross-group Settle Sheet — opened from To Pay/Receive cards */}
      <SettleSheet
        open={!!settleSheetTab}
        onOpenChange={(o) => !o && setSettleSheetTab(null)}
        defaultTab={settleSheetTab || 'owe'}
      />

      {/* iter125 — Quick Pay sheets */}
      <QrScanSheet open={qrScanOpen} onOpenChange={setQrScanOpen} />
      <PayByPhoneSheet open={phonePayOpen} onOpenChange={setPhonePayOpen} />
      <PersonalQrModal open={showMyQrOpen} onOpenChange={setShowMyQrOpen} user={user} />

      {/* iter127 — universal Share Invite sheet (from Home group cards + long-press) */}
      <ShareInviteSheet
        open={invite.open}
        onOpenChange={invite.setOpen}
        group={invite.groupCtx}
        code={invite.code}
        loading={invite.loading}
      />
    </div>
  );
}

/* ============================================================== */
/* iter125 helpers — Quick Pay tappable cards + Personal QR modal   */
/* ============================================================== */
function QuickPayCard({ icon: Icon, label, hint, accent, onClick, testId }) {
  const accentCls = {
    primary: 'bg-primary/10 text-primary border-primary/30',
    violet: 'bg-violet-500/10 text-violet-600 dark:text-violet-300 border-violet-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-500/30',
  }[accent] || 'bg-muted text-foreground border-border';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-2xl border-2 ${accentCls} hover:scale-[1.03] active:scale-[0.97] transition-transform focus:outline-none focus:ring-2 focus:ring-primary/40`}
    >
      <Icon className="w-5 h-5" strokeWidth={2.4} />
      <span className="text-xs font-bold uppercase tracking-wider leading-tight text-center">{label}</span>
      {hint && (
        <span className="text-xs text-muted-foreground leading-tight text-center normal-case font-medium" data-testid={`${testId}-hint`}>
          {hint}
        </span>
      )}
    </button>
  );
}

function PersonalQrModal({ open, onOpenChange, user }) {
  // Tiny wrapper that shows the existing PersonalQrCard inside a sheet, since
  // PersonalQrCard already encodes a URL-form QR (iter125) and has Save/Share.
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={() => onOpenChange(false)}
      data-testid="show-my-qr-overlay"
    >
      <div className="w-full max-w-md bg-background rounded-3xl p-4 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <PersonalQrCard user={user} defaultRevealed />
        <button
          onClick={() => onOpenChange(false)}
          className="w-full py-2 text-sm text-muted-foreground"
          data-testid="show-my-qr-close"
        >
          Close
        </button>
      </div>
    </div>
  );
}
